const ENDPOINT = '/api/client-error';
const MAX_ERRORS_PER_SESSION = 5;

// Extensions that inject scripts into pages — their errors are not ours
const EXTENSION_ORIGINS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
];

let errorCount = 0;
const seenMessages = new Set<string>();

function isExtensionError(filename: string | null | undefined): boolean {
  if (!filename) {
    return false;
  }
  return EXTENSION_ORIGINS.some((prefix) => filename.startsWith(prefix));
}

function send(payload: {
  message: string;
  stack?: string;
  url: string;
  line?: number;
  col?: number;
  timestamp: string;
}): void {
  if (errorCount >= MAX_ERRORS_PER_SESSION) {
    return;
  }

  // Deduplicate by message — same error firing repeatedly adds no value
  if (seenMessages.has(payload.message)) {
    return;
  }

  errorCount += 1;
  seenMessages.add(payload.message);

  const blob = new Blob([JSON.stringify(payload)], {
    type: 'application/json',
  });

  navigator.sendBeacon(ENDPOINT, blob);
}

export function initErrorReporter(): void {
  if (window.location.hostname === 'localhost') {
    return;
  }

  window.onerror = (
    event: Event | string,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error,
  ): boolean => {
    const message = typeof event === 'string' ? event : String(event);

    // "Script error." with no source = cross-origin script blocked by CORS — not actionable
    if (message === 'Script error.' && !source) {
      return false;
    }

    if (isExtensionError(source)) {
      return false;
    }

    send({
      message: message.slice(0, 300),
      stack: error?.stack?.slice(0, 3000),
      url: (source ?? window.location.href).slice(0, 500),
      line: lineno,
      col: colno,
      timestamp: new Date().toISOString(),
    });

    return false;
  };

  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent): void => {
      const reason = event.reason;
      const message: string =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'Unhandled promise rejection';

      const stack: string | undefined =
        reason instanceof Error ? reason.stack?.slice(0, 3000) : undefined;

      send({
        message: message.slice(0, 300),
        stack,
        url: window.location.href.slice(0, 500),
        timestamp: new Date().toISOString(),
      });
    },
  );
}
